// frontend/app/components/lessonPlans/teachsense/TeachSensePriorKnowledge.js
//
// What the class already thought, before the lesson taught them anything.
//
// Wonder predictions carry evidence weight 0 by design — they precede
// instruction, and scoring them would punish honest guessing. They are recorded
// as prior knowledge and shown to the teacher in real time, which is what this
// section is: answers grouped under the prompt that asked for them, so a
// teacher reads the shape of the room rather than a stack of individual rows.
//
// Where the AI wrote back a note, it is shown with its source labelled. The
// evaluator falls back to a deterministic scorer when the model is unavailable,
// and feedback the model did not write must not be presented as though it did.

import React from "react";
import { StyleSheet, Text, View } from "react-native";

import { Pill } from "../blocks/LessonBlockShell";
import { Section, StudentRow } from "./TeachSenseSection";
import { feedbackSource, feedbackText, fill } from "./teachsenseText";

const CONFIDENCE_MAX = 5; // the prediction block captures a 1..5 self-report

// Prediction rows are washed in the same blue the info token resolves to; a hex
// because tint() blends from one.
const PREDICTION_HEX = "#3b82f6";

/**
 * One heading per prompt, answers underneath, in the order the payload sends
 * them. Builds new group objects rather than touching the entries themselves.
 */
export const groupByPrompt = (entries) => {
  const order = [];
  const byKey = new Map();
  entries.forEach((entry) => {
    const key = String(entry?.block_id ?? entry?.prompt ?? "");
    if (!byKey.has(key)) {
      byKey.set(key, { key, prompt: String(entry?.prompt || ""), entries: [] });
      order.push(key);
    }
    byKey.get(key).entries.push(entry);
  });
  return order.map((key) => byKey.get(key));
};

const PredictionRow = ({ entry, colors, isDarkMode, divided, t }) => {
  const text = String(entry?.text || "").trim();
  const confidence = Number(entry?.confidence);
  const hasConfidence = Number.isFinite(confidence) && confidence > 0;
  const note = feedbackText(entry?.ai_feedback);
  const source = feedbackSource(entry?.ai_feedback);

  return (
    <StudentRow
      entry={entry}
      colors={colors}
      isDarkMode={isDarkMode}
      accent={colors.info}
      hex={PREDICTION_HEX}
      divided={divided}
      detail={text}
      trailing={
        hasConfidence ? (
          <Pill
            colors={colors}
            label={fill(t("Confidence {n}/{max}"), {
              n: Math.round(confidence),
              max: CONFIDENCE_MAX,
            })}
            accent={colors.textSecondary}
            background={colors.subtleBg}
          />
        ) : null
      }
    >
      <Text style={[styles.answer, { color: colors.textSecondary }]}>
        {text || t("No answer recorded")}
      </Text>
      {note ? (
        <View
          style={[
            styles.note,
            { backgroundColor: colors.aiCardBg, borderColor: colors.aiCardBorder },
          ]}
        >
          <Text style={[styles.noteSource, { color: colors.accentPrimary }]}>
            {source === "heuristic" ? t("Automatic check") : t("Saras AI")}
          </Text>
          <Text style={[styles.noteText, { color: colors.textSecondary }]}>{note}</Text>
        </View>
      ) : null}
    </StudentRow>
  );
};

const TeachSensePriorKnowledge = ({
  priorKnowledge = null,
  colors,
  isDarkMode = false,
  isNarrow = false,
  t,
}) => {
  const translate = t || ((value) => value);
  const entries = Array.isArray(priorKnowledge) ? priorKnowledge : [];
  const groups = groupByPrompt(entries);

  return (
    <Section
      testID="teachsense-prior-knowledge"
      colors={colors}
      isNarrow={isNarrow}
      icon="head-question-outline"
      accent={colors.warning}
      title={translate("Prior knowledge")}
      subtitle={translate("Predictions made before the lesson. Recorded, never scored.")}
      count={entries.length || null}
      isEmpty={groups.length === 0}
      empty={translate("No predictions have been recorded yet.")}
    >
      {groups.map((group, groupIndex) => (
        <View
          key={group.key || `prompt-${groupIndex}`}
          style={groupIndex > 0 ? styles.group : null}
        >
          {group.prompt ? (
            <Text style={[styles.prompt, { color: colors.textPrimary }]}>{group.prompt}</Text>
          ) : null}
          {group.entries.map((entry, index) => (
            <PredictionRow
              key={`${group.key}-${entry.student_email || index}`}
              entry={entry}
              colors={colors}
              isDarkMode={isDarkMode}
              divided={index > 0}
              t={translate}
            />
          ))}
        </View>
      ))}
    </Section>
  );
};

const styles = StyleSheet.create({
  group: { marginTop: 16 },
  prompt: { fontSize: 14, fontWeight: "700", lineHeight: 20, marginBottom: 2, maxWidth: 720 },
  answer: { fontSize: 13, fontWeight: "500", lineHeight: 19, marginTop: 2, maxWidth: 720 },
  note: {
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginTop: 8,
  },
  noteSource: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 3,
  },
  noteText: { fontSize: 12, lineHeight: 18 },
});

export default TeachSensePriorKnowledge;
